import { Divider, Stack, Typography } from '@mui/material';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';
import type { FieldRisk } from './use-field-risks';

/** Only High and Medium reach the sidebar — `useFieldRisks` filters the rest out. */
const RISK_STYLES: Record<'high' | 'medium', { label: () => string; type: keyof typeof CHIP_COLORS }> = {
    high: { label: () => _('Alto'), type: 'danger' },
    medium: { label: () => _('Medio'), type: 'warning' },
};

/**
 * Sidebar recap of the chosen field, shown under step 1 once it's complete: name, size and
 * location, plus the diseases the zone is exposed to — the same risks that drive each
 * hybrid's defensive profile.
 */
function FieldSummary({ field, risks }: { field: Field; risks: FieldRisk[] }) {
    const units = useUnitProvider();
    const area = field.area ? convertAreaFromM2(field.area, units).toString() : undefined;
    const subtitle = [area, field.location].filter(Boolean).join(' - ');

    return (
        <Stack spacing={1} p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
            <Stack>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {field.name}
                </Typography>
                {subtitle && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {subtitle}
                    </Typography>
                )}
            </Stack>
            {risks.length > 0 && (
                <>
                    <Divider />
                    <Stack spacing={1}>
                        <Typography variant='caption' fontWeight={700} color='primary'>
                            {_('Incidencia en la zona')}
                        </Typography>
                        {risks.map((risk) => {
                            const style = RISK_STYLES[risk.risk as 'high' | 'medium'];
                            return (
                                <Stack
                                    key={risk.cornMetricId}
                                    direction='row'
                                    alignItems='center'
                                    justifyContent='space-between'
                                    gap={1}
                                >
                                    <Typography variant='caption' color='primary'>
                                        {risk.name}
                                    </Typography>
                                    <ChipLabel label={style.label()} type={style.type} size='sm' />
                                </Stack>
                            );
                        })}
                    </Stack>
                </>
            )}
        </Stack>
    );
}

export default FieldSummary;
